'use client';

import cx from 'classnames';

import { useTrackPageViewAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { NormalizedAgency } from '../../../resources/agency/api/pwapi/normalizers';
import { Agency } from '../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { Itinerary } from '../../../resources/itinerary/domain/types/Itinerary';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import { Slice } from './page';
import style from './PageView.module.css';
import { SliceView } from './partials/SliceView';

export type DestinationData = PickDomain<
    Destination,
    | 'id'
    | 'slug'
    | 'name'
    | 'preposition'
    | 'continent'
    | 'coverPicture'
    | 'alertMessage'
    | 'isAvailableForQuoteRequest'
    | 'hasCommunityContent',
    {
        description: string | null;
        geolocation: Destination['geolocation'] | null;
        contentUpdatedAt: string | null;
        contentPublishedAt: string | null;
        parentDestination: PickDomain<
            Destination,
            | 'id'
            | 'slug'
            | 'name'
            | 'preposition'
            | 'continent'
            | 'coverPicture'
            | 'alertMessage'
            | 'isAvailableForQuoteRequest'
            | 'hasCommunityContent'
        > | null;
    }
>;

export type ItineraryData = PickDomain<
    Itinerary,
    'id' | 'slug' | 'title' | 'picture' | 'startingPrice' | 'duration',
    {
        destination: DestinationData;
        agency: PickDomain<Agency, 'id' | 'slug' | 'betterTripIndex'>;
    }
>;

export type AdditionalData = {
    destinations: Record<DestinationData['id'], DestinationData> | null;
    itineraries: Record<ItineraryData['id'], ItineraryData> | null;
    agencies: NormalizedAgency[] | null;
};

type PageViewProps = {
    slices: Array<Slice | Slice[]>;
    additionalData: AdditionalData;
};

export function PageView({ slices, additionalData }: PageViewProps) {
    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        ampli.viewedPageBrand({
            ...defaultProperties,
            components_list: slices.flatMap((slice) => {
                if (Array.isArray(slice)) {
                    return slice.map((s) => s.type);
                }
                return slice.type;
            }),
        });
    });
    return (
        <>
            {slices.map((slice, index) => {
                // Wrapp post block slice into a row container to display on 2 columns
                if (Array.isArray(slice)) {
                    return (
                        <WrapperGridContent key={`grid-slice-${index}`}>
                            {slice.map((s, i) => (
                                <div
                                    className={cx('col-12 col-lg-6', style.postBlock)}
                                    key={`grid-item-${index}-${i}`}
                                >
                                    <SliceView
                                        slice={s}
                                        sliceIndex={index}
                                        gridSliceIndex={index}
                                        additionalData={additionalData}
                                    />
                                </div>
                            ))}
                        </WrapperGridContent>
                    );
                }
                if (slice.format === 'center') {
                    return (
                        <WrapperCenteredContent key={`center-slice-${index}`} slice={slice}>
                            <SliceView
                                slice={slice}
                                sliceIndex={index}
                                additionalData={additionalData}
                            />
                        </WrapperCenteredContent>
                    );
                }
                if (slice.format === 'full') {
                    return (
                        <WrapperCoverContent key={`full-slice-${index}`} slice={slice}>
                            <SliceView
                                slice={slice}
                                sliceIndex={index}
                                additionalData={additionalData}
                            />
                        </WrapperCoverContent>
                    );
                }
            })}
        </>
    );
}

const WrapperCenteredContent = ({
    children,
    slice,
}: {
    children: React.ReactNode;
    slice: Slice;
}) => {
    return (
        <WrapperBase slice={slice}>
            <section className={cx('main-container')}>{children}</section>
        </WrapperBase>
    );
};

const WrapperCoverContent = ({ children, slice }: { children: React.ReactNode; slice: Slice }) => {
    return (
        <WrapperBase slice={slice}>
            <section className={cx('main-container-cover')}>{children}</section>
        </WrapperBase>
    );
};

const WrapperGridContent = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="layout-with-centered-content">
            <section className={cx('main-container')}>
                <div className="row">{children}</div>
            </section>
        </div>
    );
};

const WrapperBase = ({ children, slice }: { children: React.ReactNode; slice: Slice }) => {
    const background = 'version' in slice && slice.haveBackground ? slice.version : undefined;
    const isDarkBackground = 'version' in slice && slice.version === 'green';

    return (
        <div
            className={cx('layout-with-centered-content', {
                ['backgroundGreen']: background === 'green',
                ['backgroundYellow']: background === 'yellow',
                ['backgroundWhite']: background === 'white',
                ['textBlack']: !isDarkBackground,
                ['textWhite']: isDarkBackground,
            })}
        >
            {children}
        </div>
    );
};
